<template>
    <wizard
        :title="headerTitle"
        :steps="steps"
        :current-step="currentStep"
        :is-complete="submissionComplete"
        :loading="loading"
    >
        <template v-if="currentStep === 1">
            <generic-product-questions
                class="wizard-step-form"
                :submission="submission"
                :quoteTypes="quoteTypes"
                :facilities="facilities"
                :products="products"
                :currencies="currencies"
                :currency-code="currencyCode"
                :role="role"
                :isConvertingFromManualSubmission="isConvertingFromManualSubmission"
                @submission:reset-structure="$emit('submission:reset-structure', $event)"
                @submission:navigate-to="$emit('submission:navigate-to', $event)"
                @submission:cancel="cancelSubmission"
                :current-step="currentStep"
            />
        </template>

        <template v-else-if="currentStep === 2">
            <product
                class="wizard-step-form"
                v-model="submission"
                :currency-code="currencyCode"
                :facilities="facilities"
                :commitment="commitment"
                @submission:navigate-to="$emit('submission:navigate-to', $event)"
                @submission:cancel="cancelSubmission"
                :current-step="currentStep"
            />
        </template>

        <template v-else-if="currentStep === 3">
            <select-insurers
                v-if="isStandalonePolicy"
                 class="wizard-step-form"
                 :submission="submission"
                 :selectedInsurersUsersList.sync="selectedInsurersUsersList"
                 :insurers.sync="insurers"
                 :current-step="currentStep"
                 :is-manual-firm-order=true
                 @submission:navigate-to="$emit('submission:navigate-to', $event)"
            />

            <additional-information
                v-else
                class="wizard-step-form"
                v-model="submission"
                @submission:navigate-to="$emit('submission:navigate-to', $event)"
                @submission:save="$emit('submission:save', $event)"
                @submission:cancel="cancelSubmission"
                :current-step="currentStep"
            />
        </template>

        <template v-else-if="currentStep === 4">
            <manual-firm-order
                v-if="isStandalonePolicy"
                class="wizard-step-form"
                :submission="submission"
                :selectedInsurersUsersList="selectedInsurersUsersList"
                :insurers="insurers"
                :current-step="currentStep"
                @submission:navigate-to="$emit('submission:navigate-to', $event)"
                @submission:complete="handleSubmissionComplete"
                @standalonePolicy:manualFirmOrderDraft="handleStandalonePolicyManualFirmOrderDraft"
                :waitingPeriods="waitingPeriods"
            />

            <broker-summary
                v-else
                class="wizard-step-summary"
                :submission="submission"
                :current-step="currentStep"
                :facilities="facilities"
                :isConvertingFromManualSubmission="isConvertingFromManualSubmission"
                @submission:navigate-to="$emit('submission:navigate-to', $event)"
            />
        </template>

        <template v-else-if="currentStep === 5">
            <broker-summary
                v-if="isStandalonePolicy"
                class="wizard-step-summary"
                :submission="submission"
                :current-step="currentStep"
                :facilities="facilities"
                :selectedInsurers="selectedInsurers"
                :standalonePolicyFirmOrderData="standalonePolicyFirmOrderData"
                :isConvertingFromManualSubmission="isConvertingFromManualSubmission"
                @submission:navigate-to="$emit('submission:navigate-to', $event)"
            />

            <select-insurers
                v-else
                class="wizard-step-form"
                :submission="submission"
                :selectedInsurersUsersList.sync="selectedInsurersUsersList"
                :insurers.sync="insurers"
                :current-step="currentStep"
                :is-manual-firm-order="submission.is_manual_firm_order === 1"
                @submission:navigate-to="$emit('submission:navigate-to', $event)"
                @submission:complete="submissionComplete = true"
            />
        </template>

        <template v-else-if="currentStep === 6">
            <manual-firm-order
                class="wizard-step-form"
                :submission="submission"
                :selectedInsurersUsersList="selectedInsurersUsersList"
                :insurers="insurers"
                :current-step="currentStep"
                @submission:navigate-to="$emit('submission:navigate-to', $event)"
                @submission:complete="handleSubmissionComplete"
                v-if="waitingPeriods && Object.keys(waitingPeriods).length > 0"
                :waitingPeriods="waitingPeriods"
            />
        </template>

        <modal
            ref="unsavedModal"
            title="Return to dashboard?"
            confirmation
        >
            <p>Are you sure you want to go to dashboard? (progress will be lost)</p>
        </modal>
    </wizard>
</template>

<script>
    import Wizard from './UIComponents/Wizard.vue';
    import GenericProductQuestions from './SubmissionWizard/GenericProductQuestions';
    import BrokerSummary from './SubmissionWizard/BrokerSummary';
    import SelectInsurers from './SubmissionWizard/SelectInsurers';
    import AdditionalInformation from './SubmissionWizard/AdditionalInformation';
    import Product from '~/components/Broker/Products/Product';
    import Modal from '~/components/Shared/UIComponents/Modal';
    import ManualFirmOrder from "./SubmissionWizard/ManualFirmOrder";
    import submissionHelpers from "../../mixins/submissionHelpers";

    export default {
        name: 'SubmissionWizard',
        props: {
            value: Object,
            commitment: Object,
            currentStep: Number,
            isConvertingFromManualSubmission: {
                type: Boolean,
                default: false
            }
        },
        components: {
            Wizard,
            GenericProductQuestions,
            BrokerSummary,
            SelectInsurers,
            AdditionalInformation,
            ManualFirmOrder,
            Product,
            Modal,
        },
        mixins: [submissionHelpers],
        data() {
            return {
                loading: true,
                quoteTypes: [],
                facilities: [],
                products: [],
                currencies: [],
                insurers: [],
                selectedInsurersUsersList: {},
                submissionComplete: false,
                standalonePolicyFirmOrderData: {},
            }
        },
        computed: {
            headerTitle() {
                return this.isInvoice ? 'Create invoice' : 'Get quotes';
            },
            steps() {
                const steps = [
                    'Build Product',
                    'Details'
                ];

                if (this.isStandalonePolicy) {
                    steps.push(
                        'Select Insurers',
                        'Manual Firm Order',
                        'Summary',
                    );
                } else {
                    steps.push(
                        'Additional Information',
                        'Summary',
                        'Select Insurers',
                    );
                }

                return steps;
            },
            isManualFirmOrderStep() {
                return this.currentStep === 6 || (this.currentStep === 4 && this.isStandalonePolicy);
            },
            submission: {
                get() {
                    return this.value;
                },
                set(submission) {
                    this.$emit('input', submission);
                }
            },
            role() {
                return this.$root.user.company ? this.$root.user.company.company_type : null;
            },
            currencyCode() {
                const selectedCurrency = this.currencies.find(currency => this.submission.currency_id === currency.id) || {};
                return selectedCurrency.code || '';
            },
            insurerType() {
                if (this.submission.hasOwnProperty('captive_insurer_company_id') && this.submission.captive_insurer_company_id) {
                    return 'reinsurer';
                }

                return 'insurer'
            },
            waitingPeriods() {
                return this.$root.waitingPeriods ? this.$root.waitingPeriods : [];
            },
            isInvoice() {
                return Boolean(this.commitment) || Boolean(this.submission.commitment_declaration);
            },
            selectedInsurers() {
                const insurerIds = Object.keys(this.selectedInsurersUsersList).map((insurerId) => parseInt(insurerId));
                return this.insurers.filter((insurer) => {
                    return insurerIds.includes(insurer.id)
                });
            }
        },
        watch: {
            value: {
                handler() {
                    this.$emit('input', this.value);
                },
                deep: true
            },
            currentStep(newCurrentStep, oldCurrentStep) {
                if (this.isStandalonePolicy && newCurrentStep === 3 && oldCurrentStep === 2) {
                    this.$emit('submission:save', newCurrentStep - 1);
                }
            }
        },
        mounted() {
            const mountRequests = [];
            const facilitiesUrl = this.isSubmissionInDraft() ? 'facility'  : 'facility/status/1';

            mountRequests.push(axios.get(this.$root.api('quote-type')).then(res => {
                this.quoteTypes = res.data.data
            }));
            mountRequests.push(axios.get(this.$root.api(facilitiesUrl)).then(res => {
                this.facilities = res.data.data
            }));
            mountRequests.push(axios.get(this.$root.api('product')).then(res => {
                this.products = res.data.data
            }));
            mountRequests.push(axios.get(this.$root.api('currency')).then(res => {
                this.currencies = res.data.data
            }));

            axios.all(mountRequests).then(() => {
                this.checkAndApplyCommitment();
                this.loading = false;
            });

            this.setSelectedInsurersFromFirmOrderDraft();

            if (this.isConvertingFromManualSubmission || this.isConvertedFromManualSubmission) {
                this.selectedInsurersUsersList = this.submission.insurers?.reduce((selectedUsers, insurer) => {
                    selectedUsers[insurer.insurer_company_id] = [insurer.underwriter_id];
                    return selectedUsers;
                }, {});
            }
        },
        methods: {
            setSelectedInsurersFromFirmOrderDraft() {
                if (!this.submission.manual_firm_order_draft_data) {
                    return;
                }

                this.selectedInsurersUsersList = this.submission.manual_firm_order_draft_data.data.offers.reduce((selectedUsers, offer) => {
                    selectedUsers[offer.insurer_company_id] = offer.selectedUsers;
                    return selectedUsers;
                }, {});
            },
            async cancelSubmission() {
                if (await this.$refs.unsavedModal.open() === false) {
                    return;
                }

                this.$router.push('/agent/dashboard');
            },
            async handleSubmissionComplete() {
                if (this.isInvoice) {
                    this.$router.go(0);
                    return false;
                } else if (this.isStandalonePolicy) {
                    this.submission = (await axios.get(this.$root.api(`broker/submission/${this.$route.params.id}`))).data.data;
                    this.$emit('submission:navigate-to', this.currentStep + 1);
                    return false;
                }

                this.$router.push('/agent/dashboard');
            },
            handleStandalonePolicyManualFirmOrderDraft(manualFirmOrderData) {
                this.standalonePolicyFirmOrderData = {...manualFirmOrderData};
            },
            isSubmissionInDraft() {
                return this.submission.status === 'draft';
            },
            checkAndApplyCommitment() {
                if (this.submission.commitment_declaration) {
                    // skip first step
                    this.$emit('submission:navigate-to', this.currentStep + 1);
                } else if (this.commitment) {
                    const invoiceQuoteType = this.quoteTypes.find(qt => qt.key === 'invoice');
                    this.submission.commitment_declaration_id = this.commitment.id;
                    this.submission.quote_type_id = invoiceQuoteType.id;
                    this.submission.quote_type = invoiceQuoteType;
                    this.submission.risk_structure = 'accepted_declaration';
                    this.submission.is_manual_firm_order = 1;
                    this.submission.facility_id = this.commitment.facility_id;
                    this.submission.client_company_id = this.commitment.client_company_id;
                    this.submission.placing_broker_company_id = this.commitment.placing_broker_company_id;
                    this.submission.producing_broker_company_id = this.commitment.producing_broker_company_id;
                    this.submission.captive_insurer_company_id = this.commitment.captive_insurer_company_id;
                    this.submission.category_id = this.commitment.category_id;
                    this.submission.product_id = this.commitment.product_id;
                    this.submission.product = this.commitment.product;
                    this.submission.currency_id = this.commitment.currency_id;
                    this.submission.country_id = this.commitment.country_id;

                    if (this.commitment.obligors.length > 0) {
                        this.submission.obligors = [];

                        this.commitment.obligors.forEach(obligor => {
                            this.submission.obligors.push({
                                country_id: obligor.country_id,
                                pivot: {
                                    obligor_id: obligor.pivot.obligor_id,
                                    sub_limit: obligor.pivot.sub_limit
                                }
                            })
                        });
                    }

                    if (this.commitment.guarantors.length > 0) {
                        this.submission.guarantors = [];

                        this.commitment.guarantors.forEach(guarantor => {
                            this.submission.guarantors.push({
                                country_id: guarantor.country_id,
                                pivot: {
                                    guarantor_id: guarantor.pivot.guarantor_id
                                }
                            })
                        });
                    }

                    // skip first step
                    this.$emit('submission:navigate-to', this.currentStep + 1);
                }
            }
        }
    }
</script>
